import { createElement, useMemo, useRef } from 'react';
import { setAlpha } from '../../../design-tokens';
import { SelectBoxDesignProps, SelectBoxStatus } from './';
import { postfixTestId } from '../../../util/testId';
import { setRef } from '../../../util/ref-util';
import { renderSlotWithComponentFallback } from '../../../slot';
import { Icon } from '../../Icon';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { useDesignTokens, renderInlineCssVars, DesignTokens } from '../../../theme';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { ErrorStatus, WarningStatus, ArrowSmallDownBtn } from '@sas/react-svg';
import { InvisibleText } from '../../InvisibleText';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import bundle from '../NovaComponents-Select-gui-icu.properties';

import cx from 'classnames';
import cn from '../Select.css.json';
import '../Select.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

const useHtmlId = createUseHtmlId('Select');

/**
 * @internal
 */
export const SelectBoxDesign = (props: SelectBoxDesignProps) => {
  const localize = useTextLocalizer();
  const focusable = props.focusable ?? true;
  const dt = useDesignTokens();

  const tokenOverrides = useMemo(() => {
    const getOverrides = props.theme;
    let tokens;
    if (getOverrides) {
      // Ensure that `--select-placeholder-fg` isn't being set to undefined to avoid
      // overriding the default placeholder foreground token
      const placeholderFg = getOverrides({ state: 'placeholder' })?.fg;
      tokens = {
        ...renderInlineCssVars(getOverrides({ state: 'normal' }), 'select'),
        ...renderInlineCssVars(getOverrides({ state: 'hover' }), 'select-hover'),
        ...renderInlineCssVars(getOverrides({ state: 'active' }), 'select-active'),
        ...renderInlineCssVars(placeholderFg ? { fg: placeholderFg } : {}, 'select-placeholder'),
      };
    }

    return tokens;
  }, [props.theme]);

  const style = {
    ...renderInlineCssVars(
      {
        'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
        ...getSelectBoxDesignTokens(dt),
        ...getStatusTokens(dt, props.status),
      },
      'select'
    ),
    ...renderInlineCssVars(
      {
        'squish-s': dt.spacing.insetSquish.s.padding,
        xs: dt.spacing.core.xs,
      },
      'select-spacing'
    ),
    ...props.font,
    ...tokenOverrides,
    ...props.style,
  };

  const showStatus = props.status === 'alert' || props.status === 'warning';
  const showStatusIcon = !props.hasFocus && showStatus;
  const statusIcon = showStatusIcon ? (
    props.status === 'alert' ? (
      <Icon icon={ErrorStatus} color="alert" />
    ) : (
      <Icon icon={WarningStatus} color="warning" />
    )
  ) : undefined;

  const textRef = useRef<HTMLElement | null>(null);
  const setTruncationRef = useTruncationTooltip(props.innerRef, textRef);
  const itemIconId = useHtmlId();
  const itemIcon = props.icon ? (
    <div className={cn.itemIcon}>
      <Icon
        id={itemIconId}
        icon={props.icon}
        tooltip={props.iconTooltip}
        ariaLabel={props.iconAriaLabel}
        disabled={props.disabled}
        testId={postfixTestId(props.testId, 'itemIcon')}
      />
    </div>
  ) : undefined;
  const invisibleTextId = useHtmlId();
  const innerTextId = useHtmlId();
  const ariaDescribedBy: string[] = props.required ? [invisibleTextId] : [];

  if (Array.isArray(props.ariaDescribedBy)) {
    ariaDescribedBy.push(...props.ariaDescribedBy);
  } else if (props.ariaDescribedBy !== undefined) {
    ariaDescribedBy.push(props.ariaDescribedBy);
  }

  const ariaLabelledBy: string[] = [];

  if (Array.isArray(props.ariaLabelledBy)) {
    ariaLabelledBy.push(...props.ariaLabelledBy);
  } else if (props.ariaLabelledBy !== undefined) {
    ariaLabelledBy.push(props.ariaLabelledBy);
  }

  const ariaLabel: string[] = [];
  const getIconA11yInfo = useIconA11yInfo();
  if (props.readOnly) {
    // in this setup, make sure the icon, if available, and inner
    // text is also read by the SR
    if (ariaLabelledBy.length !== 0) {
      if (props.icon) ariaLabelledBy.push(itemIconId);
      ariaLabelledBy.push(innerTextId);
    }
    // note that ariaLabel is ignored by SR if already using ariaLabelledBy
    if (props.ariaLabel) {
      ariaLabel.push(props.ariaLabel);
      if (ariaLabelledBy.length === 0) {
        if (props.icon) {
          const { tooltip } = getIconA11yInfo({
            icon: props.icon,
            tooltip: props.iconTooltip,
            ariaLabel: props.iconAriaLabel,
          });
          if (tooltip) ariaLabel.push(tooltip);
        }
        ariaLabel.push(props.label ?? '');
      }
    }
  }

  /* NOTE: we can't use button on dropdown selectBox because it can't receive focus on FF+MacOs
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Clicking_and_focus */
  return props.readOnly ? (
    <div
      ref={props.tooltip ? props.innerRef : setTruncationRef}
      role="text"
      aria-disabled={props.disabled}
      aria-describedby={ariaDescribedBy.length ? ariaDescribedBy.join(' ') : undefined}
      aria-label={ariaLabel.join(' ')}
      aria-labelledby={ariaLabelledBy.length ? ariaLabelledBy.join(' ') : undefined}
      title={props.tooltip}
      className={cx(props.className, cn.readOnly, cn.focusVisible, props.disabled && cn.disabled)}
      tabIndex={focusable ? 0 : -1} // If set tabIndex to undefined rather than -1, it does not fire onFocus event at all
      data-testid={props.testId}
      style={{ ...style, ...props.style }}
    >
      <div className={cn.textWrapper}>
        {itemIcon}
        <span
          id={innerTextId}
          ref={(el) => {
            setRef(props.textRef, el);
            setRef(textRef, el);
          }}
          className={cx(cn.label, props.disabled && cn.labelDisabled)}
          data-testid={postfixTestId(props.testId, 'readOnlyValue')}
        >
          {props.label ?? ''}
        </span>
      </div>
    </div>
  ) : (
    <div
      ref={props.tooltip ? props.innerRef : setTruncationRef}
      role="combobox"
      aria-haspopup="listbox"
      aria-controls={props.listId}
      aria-expanded={props.isOpen ? 'true' : 'false'}
      aria-activedescendant={props.ariaActiveDescendant}
      aria-disabled={props.disabled}
      aria-invalid={props.status === 'alert' ? true : undefined}
      aria-describedby={ariaDescribedBy.length ? ariaDescribedBy.join(' ') : undefined}
      aria-label={props.ariaLabel}
      aria-labelledby={ariaLabelledBy.length ? ariaLabelledBy.join(' ') : undefined}
      title={props.tooltip}
      className={cx(
        props.className,
        cn.select,
        !props.isOpen && cn.focusVisible,
        props.disabled && cn.disabled,
        props.isOpen && cn.expanded,
        showStatus ? cn.status : undefined
      )}
      tabIndex={focusable ? 0 : -1} // If set tabIndex to undefined rather than -1, it does not fire onFocus event at all
      data-testid={props.testId}
      style={{ ...style, ...props.style }}
      onClick={() => {
        props.toggleOpen?.();
        props.onPress?.();
      }}
      onKeyDown={(e) => {
        if (e.key === ' ') {
          props.toggleOpen?.();
        }
        props.onKeyDown?.(e);
      }}
    >
      {props.required ? (
        <InvisibleText id={invisibleTextId}>{localize('required.aria')}</InvisibleText>
      ) : null}
      <div className={cn.textWrapper}>
        {itemIcon}
        <span
          id={innerTextId}
          ref={(el) => {
            setRef(props.textRef, el);
            setRef(textRef, el);
          }}
          className={cx(
            !showStatus && props.isPlaceholder ? cn.labelPlaceholder : cn.label,
            props.labelClassName,
            props.disabled && cn.labelDisabled
          )}
          aria-live={!props.isOpen ? 'polite' : undefined}
          data-testid={postfixTestId(props.testId, 'value')}
        >
          {props.label ?? ''}
        </span>
      </div>
      {props.status || showStatusIcon ? <div className={cn.statusIcon}>{statusIcon}</div> : null}
      {renderSlotWithComponentFallback(props.renderIcon, Icon, {
        className: cn.selectIcon,
        disabled: props.disabled,
        testId: postfixTestId(props.testId, 'icon'),
        icon: ArrowSmallDownBtn,
        role: 'presentation',
      })}
    </div>
  );
};

const getStatusTokens = (dt: DesignTokens, status: SelectBoxStatus) => {
  switch (status) {
    case 'alert':
      return {
        'status-bd': dt.stateStatusNova.alert.bd,
        'status-fg': dt.stateStatusNova.alert.fg,
      };
    case 'warning':
      return {
        'status-bd': dt.stateStatusNova.warning.bd,
        'status-fg': dt.stateStatusNova.warning.fg,
      };
    default:
      return {};
  }
};

const getSelectBoxDesignTokens = (dt: DesignTokens) => {
  if (dt.enableNovaTokens) {
    return {
      ...dt.stateStandardNova.normal,
      'hover-bd': dt.stateStandardNova.hover.bd,
      'hover-bg': dt.stateStandardNova.hover.bg,
      'hover-fg': dt.stateStandardNova.hover.fg,
      'disabled-opacity': 1,
      'placeholder-fg': dt.c.colorTextLabel,
      'placeholder-active-fg': dt.c.colorTextDefault,
      'active-bd': dt.stateStandardNova.active.bd,
      'active-bg': dt.stateStandardNova.active.bg,
      'active-fg': dt.stateStandardNova.active.fg,
      'active-filter': dt.stateStandardNova.active.filter,
      'disabled-bg': dt.stateStandardNova.disabled.bg,
      'disabled-fg': dt.stateStandardNova.disabled.fg,
      'disabled-bd': dt.stateStandardNova.disabled.bd,
    };
  } else {
    switch (dt.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          ...dt.stateStandard.normal,
          'hover-bd': dt.stateStandard.hover.bd,
          'hover-bg': dt.stateStandard.hover.bg,
          'hover-fg': dt.stateStandard.hover.fg,
          'active-bd': 'dt.stateStandard.normal.bd',
          'active-filter': dt.stateStandard.active.filter,
          'disabled-opacity': 0.3,
          'placeholder-fg': dt.c.n1,
          'disabled-bg': dt.stateStandard.disabled.bg,
          'disabled-fg': dt.stateStandard.disabled.fg,
          'disabled-bd': dt.stateStandard.disabled.bd,
        };
    }
    switch (dt.themeVariant) {
      case 'dark':
        return {
          ...dt.stateStandard.normal,
          'hover-bd': dt.stateStandard.hover.bd,
          'hover-bg': dt.stateStandard.hover.bg,
          'hover-fg': dt.stateStandard.hover.fg,
          'active-bd': dt.stateStandard.active.bd,
          'active-filter': dt.stateStandard.active.filter,
          'disabled-opacity': 0.3,
          'placeholder-fg': setAlpha(dt.c.white, 0.65),
          'disabled-bg': dt.stateStandard.disabled.bg,
          'disabled-fg': dt.stateStandard.disabled.fg,
          'disabled-bd': dt.stateStandard.disabled.bd,
        };
      case 'high-contrast':
        return {
          ...dt.stateStandard.normal,
          'hover-bd': dt.stateStandard.hover.bd,
          'hover-bg': dt.stateStandard.hover.bg,
          'hover-fg': dt.stateStandard.hover.fg,
          'active-bd': dt.stateStandard.normal.bd,
          'active-filter': dt.stateStandard.active.filter,
          'disabled-opacity': 0.5,
          'placeholder-fg': dt.c.n1,
          'disabled-bg': dt.stateStandard.disabled.bg,
          'disabled-fg': dt.stateStandard.disabled.fg,
          'disabled-bd': dt.stateStandard.disabled.bd,
        };
      default:
        return {
          ...dt.stateStandard.normal,
          'hover-bd': dt.stateStandard.hover.bd,
          'hover-bg': dt.stateStandard.hover.bg,
          'hover-fg': dt.stateStandard.hover.fg,
          'active-bd': dt.stateStandard.active.bd,
          'active-filter': dt.stateStandard.active.filter,
          'disabled-opacity': 0.3,
          'placeholder-fg': dt.c.n1,
          'disabled-bg': dt.stateStandard.disabled.bg,
          'disabled-fg': dt.stateStandard.disabled.fg,
          'disabled-bd': dt.stateStandard.disabled.bd,
        };
    }
  }
};
